import Booking from "@components/Booking";
import BookingTopBar from "@components/TopBar";
import ConfirmationDialog from "@hudson/ui/components//Dialogs/Confirmation";
import Error from "@hudson/ui/components/Error";
import Button from "@hudson/ui/components/FormFields/Button";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import Success from "@hudson/ui/components/Success";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import cancelRes from "@services/cancel_res";
import readRes from "@services/read_res";
import Session from "@utils/session";
import { useRouter } from "next/navigation";
import { useState } from "react";
import OpenConversionButton from "./OpenConversionButton";
import TopBar from "./TopBar";

interface ResSummaryPage {
  className?: string;
  onBack?: () => void;
  showBack?: boolean;
  showLogout?: boolean;
}

function ResSummaryPage({
  className = "",
  onBack,
  showBack,
  showLogout = true,
}: ResSummaryPage): JSX.Element | null {
  const {
    loading: cfgLoading,
    data: cfgData,
    error: cfgError,
  } = useConfig(["singleServiceAutoSelect"]);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelError, setCancelError] = useState<string>();
  const [successMsg, setSuccessMsg] = useState<string>();
  const router = useRouter();
  const { data, loading, error } = useContent([
    "beBookAnotherButton",
    "beCancelConfirmationCancelButton",
    "beCancelConfirmationMsg",
    "beCancelResSuccess",
    "beConfirmDialogDefaultTitle",
    "beErrResNotCanceled",
    "beRessummaryHead",
    "beCancelButton",
    "beCanceledMsg",
    "beEditOpenResButton",
    "beEditResButton",
  ]);
  const booking = Session.getVerifiedBooking();
  const res = booking?.legs[0];
  const userLoggedIn = !!Session.getBooking().profiles.user;

  if (error) {
    console.error(error);
    return null;
  }

  async function cancel() {
    if (!res?.resIdInternal) return;
    setCancelError(undefined);
    const success = await cancelRes(res.resIdInternal);
    if (!success) {
      if (data?.content.beErrResNotCanceled) setCancelError(data.content.beErrResNotCanceled);
    } else {
      const booking = await readRes({ resId: res.resIdInternal });
      if (booking) Session.setVerifiedBooking(booking);
      await global.window.apolloClient.clearStore();
      setSuccessMsg(data?.content.beCancelResSuccess);
    }
  }

  function edit() {
    Session.editVerifiedBooking();
    // Don't go straight to services if single service auto-select is enabled.
    cfgData?.config.singleServiceAutoSelect
      ? router.push(`/${window.siteId}/book`)
      : router.push(`/${window.siteId}/book/services`);
  }

  return loading ? (
    <ConcentricLoader className="max-w-2xl px-4 py-10 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <div className={className}>
      <div className="flex justify-between">
        <TopBar onBack={onBack} showBack={showBack} showLogout={!userLoggedIn} />
        {userLoggedIn && !showBack && <BookingTopBar />}
      </div>

      <div className="max-w-md mx-auto sm:max-w-5xl">
        <p className="mt-2 text-base text-gray-500">{data?.content.beRessummaryHead}</p>
        {booking?.legs[0].cancelled && !successMsg && (
          <Error className="my-2" message={data?.content.beCanceledMsg} heavyStyle />
        )}
        <Booking booking={booking} />

        {cancelError && <Error className="mt-16 -mb-8" message={cancelError} heavyStyle />}
        {successMsg && <Success className="mt-16 -mb-8" message={successMsg} heavyStyle />}

        <div className="flex flex-wrap items-center justify-end mt-16 space-y-6 sm:space-x-8 sm:space-y-0">
          <Button onClick={() => router.push(`/${window.siteId}/book`)} asLink>
            {data?.content.beBookAnotherButton}
          </Button>

          {res?.editable &&
            res?.options?.allowOpenResConversion &&
            !booking?.legs[0].cancelled &&
            res?.service?.selectedTime?.pickupTime && (
              <OpenConversionButton
                resId={res.resIdInternal}
                onError={setCancelError}
                onSuccess={setSuccessMsg}
              />
            )}

          {res?.cancellable && !booking?.legs[0].cancelled && (
            <>
              <Button type="button" color="red" onClick={() => setShowCancelConfirm(true)}>
                {data?.content.beCancelButton}
              </Button>

              <ConfirmationDialog
                cancelLabel={data?.content.beCancelConfirmationCancelButton}
                confirmLabel={data?.content.beCancelButton}
                message={data?.content.beCancelConfirmationMsg}
                onConfirm={cancel}
                onHide={() => setShowCancelConfirm(false)}
                show={showCancelConfirm}
                title={data?.content.beConfirmDialogDefaultTitle}
              />
            </>
          )}
          {res?.editable && !booking?.legs[0].cancelled && (
            <Button onClick={edit} type="button" color="transparent">
              {res?.service?.selectedTime?.pickupTime
                ? data?.content.beEditResButton
                : data?.content.beEditOpenResButton}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResSummaryPage;
